import { useState, useEffect } from "react";
import { SparklesIcon, CheckCircleIcon, XCircleIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import useSettingsStore from "../../store/settingsStore";
import { llmConfigApi } from "../../api/settings";

// ── Provider / model catalogue ────────────────────────────────────────────────

const PROVIDERS = [
  { value: "anthropic",    label: "Anthropic" },
  { value: "openai",       label: "OpenAI" },
  { value: "azure_openai", label: "Azure OpenAI" },
  { value: "mistral",      label: "Mistral AI" },
];

const MODELS = {
  anthropic: [
    { value: "claude-opus-4-7",            label: "Claude Opus 4.7" },
    { value: "claude-sonnet-4-6",          label: "Claude Sonnet 4.6 (recommended)" },
    { value: "claude-haiku-4-5-20251001",  label: "Claude Haiku 4.5" },
  ],
  openai: [
    { value: "gpt-4o",      label: "GPT-4o" },
    { value: "gpt-4o-mini", label: "GPT-4o mini" },
    { value: "gpt-4.1",     label: "GPT-4.1" },
    { value: "gpt-4.1-mini",label: "GPT-4.1 mini" },
  ],
  azure_openai: null,   // free-text deployment name
  mistral: [
    { value: "mistral-large-latest",  label: "Mistral Large" },
    { value: "mistral-medium-latest", label: "Mistral Medium" },
    { value: "mistral-small-latest",  label: "Mistral Small" },
  ],
};

const DEFAULT_MODEL = {
  anthropic:    "claude-sonnet-4-6",
  openai:       "gpt-4o",
  azure_openai: "",
  mistral:      "mistral-large-latest",
};

const INPUT_CLS = `w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800
  focus:outline-none focus:ring-2 focus:ring-bh-400 bg-white disabled:bg-gray-50 disabled:text-gray-400`;

// ── Feature-flag toggle row ───────────────────────────────────────────────────

function ToggleRow({ icon: Icon, label, description, enabled, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-1.5 rounded-lg bg-gray-100">
          <Icon className="h-4 w-4 text-gray-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 focus:outline-none
          ${enabled ? "bg-bh-500" : "bg-gray-200"}`}
        role="switch"
        aria-checked={enabled}
      >
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow ring-0
          transition-transform duration-200 ${enabled ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </div>
  );
}

// ── LLM config card ───────────────────────────────────────────────────────────

const EMPTY = { provider: "anthropic", model: "claude-sonnet-4-6", api_key: "", endpoint: "", api_version: "", embed_model: "", system_prompt: "", suggest_followups_enabled: true, file_upload_enabled: true };

const EMBED_UNAVAILABLE = new Set(["anthropic"]);

const DEFAULT_PROMPT_PLACEHOLDER = `Leave blank to use the built-in turbomachinery system prompt.

Example:
You are an expert pump engineer at Brumboo. You specialise in centrifugal impeller design and always reply concisely with SI units. When a design is loaded, reference its parameters in your answers.`;

function LLMConfigCard() {
  const [form,     setForm]     = useState(EMPTY);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [error,    setError]    = useState(null);

  useEffect(() => {
    llmConfigApi.get()
      .then(({ data }) => setForm(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, [k]: val };
      if (k === "provider") {
        next.model = DEFAULT_MODEL[val] || "";
      }
      return next;
    });
    setSaved(false);
    setTestResult(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { data } = await llmConfigApi.save(form);
      setForm(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const { data } = await llmConfigApi.test(form);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.error || "Request failed" });
    } finally {
      setTesting(false);
    }
  };

  const models      = MODELS[form.provider];
  const isAzure     = form.provider === "azure_openai";
  const ragDisabled = EMBED_UNAVAILABLE.has(form.provider);

  if (loading) return <p className="text-sm text-gray-400 px-4 py-3">Loading…</p>;

  return (
    <div className="space-y-4 p-1">
      {/* Provider + Model */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Provider</label>
          <select value={form.provider} onChange={set("provider")} className={INPUT_CLS}>
            {PROVIDERS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Model</label>
          {models ? (
            <select value={form.model} onChange={set("model")} className={INPUT_CLS}>
              {models.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={form.model}
              onChange={set("model")}
              placeholder="Deployment name"
              className={INPUT_CLS}
            />
          )}
        </div>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">API Key</label>
        <input
          type="password"
          value={form.api_key}
          onChange={set("api_key")}
          placeholder="Paste key — stored server-side, never exposed"
          className={INPUT_CLS}
          autoComplete="new-password"
        />
      </div>

      {/* Azure-only fields */}
      {isAzure && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Azure Endpoint
              </label>
              <input
                type="text"
                value={form.endpoint}
                onChange={set("endpoint")}
                placeholder="https://<resource>.openai.azure.com"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                API Version
              </label>
              <input
                type="text"
                value={form.api_version}
                onChange={set("api_version")}
                placeholder="2024-02-01"
                className={INPUT_CLS}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Embedding deployment <span className="font-normal normal-case text-gray-400">(for RAG knowledge base)</span>
            </label>
            <input
              type="text"
              value={form.embed_model}
              onChange={set("embed_model")}
              placeholder="e.g. text-embedding-3-small"
              className={INPUT_CLS}
            />
          </div>
        </div>
      )}

      {/* Embedding model info — read-only for providers with a fixed model */}
      {form.provider === "openai" && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Embedding model <span className="font-normal normal-case text-gray-400">(for RAG knowledge base)</span>
          </label>
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            text-embedding-3-small · 1024 dim · fixed
          </p>
        </div>
      )}
      {form.provider === "mistral" && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Embedding model <span className="font-normal normal-case text-gray-400">(for RAG knowledge base)</span>
          </label>
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            mistral-embed · 1024 dim · fixed
          </p>
        </div>
      )}

      {/* RAG availability notice */}
      {ragDisabled && (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Anthropic does not provide an embeddings API.
          The RAG knowledge base will be unavailable with this provider.
        </p>
      )}

      {/* Test result */}
      {testResult && (
        <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm
          ${testResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
          {testResult.ok
            ? <CheckCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
            : <XCircleIcon     className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{testResult.ok ? `Connected — model replied: "${testResult.message}"` : testResult.message}</span>
        </div>
      )}

      {/* Follow-up suggestions toggle */}
      <div className="flex items-center justify-between py-1">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Follow-up suggestions</p>
          <p className="text-xs text-gray-400 mt-0.5">
            LLM proposes 2–3 clickable next questions after each substantive answer.
          </p>
        </div>
        <button
          onClick={() => { setForm(f => ({ ...f, suggest_followups_enabled: !f.suggest_followups_enabled })); setSaved(false); }}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 focus:outline-none
            ${form.suggest_followups_enabled ? "bg-bh-500" : "bg-gray-200"}`}
          role="switch"
          aria-checked={form.suggest_followups_enabled}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow ring-0
            transition-transform duration-200 ${form.suggest_followups_enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {/* File upload toggle */}
      <div className="flex items-center justify-between py-1">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">File attachment</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Allow users to attach .txt / .csv / .xml files to chat messages.
          </p>
        </div>
        <button
          onClick={() => { setForm(f => ({ ...f, file_upload_enabled: !f.file_upload_enabled })); setSaved(false); }}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 focus:outline-none
            ${form.file_upload_enabled ? "bg-bh-500" : "bg-gray-200"}`}
          role="switch"
          aria-checked={form.file_upload_enabled}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow ring-0
            transition-transform duration-200 ${form.file_upload_enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {/* System prompt */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          System prompt{" "}
          <span className="font-normal normal-case text-gray-400">(optional — leave blank for built-in default)</span>
        </label>
        <textarea
          value={form.system_prompt}
          onChange={set("system_prompt")}
          rows={8}
          placeholder={DEFAULT_PROMPT_PLACEHOLDER}
          className={`${INPUT_CLS} resize-y font-mono text-xs`}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-bh-600 text-white text-sm font-medium
            hover:bg-bh-700 disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
        </button>
        <button
          onClick={handleTest}
          disabled={testing || saving}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700
            hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
    </div>
  );
}

// ── Copilot starters card ─────────────────────────────────────────────────────

const DEFAULT_STARTERS = [
  "What nq range suits a mixed-flow impeller?",
  "Calculate D₂ for 100 m³/h at 40 m, 2950 rpm",
  "What causes a hump in the Q-H curve?",
  "Explain ISO 9906 acceptance grades",
];

function CopilotStartersCard() {
  const [questions, setQuestions] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    llmConfigApi.get()
      .then(({ data }) => setQuestions(data.starter_questions?.length ? data.starter_questions : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (idx, val) => {
    setQuestions(qs => qs.map((q, i) => i === idx ? val : q));
    setSaved(false);
  };
  const remove = (idx) => {
    setQuestions(qs => qs.filter((_, i) => i !== idx));
    setSaved(false);
  };
  const add = () => {
    setQuestions(qs => [...qs, ""]);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await llmConfigApi.save({ starter_questions: questions.filter(q => q.trim()) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setQuestions([]); setSaved(false); };

  if (loading) return <p className="text-sm text-gray-400 px-4 py-3">Loading…</p>;

  const isCustom = questions.length > 0;

  return (
    <div className="space-y-3 p-1">
      {!isCustom && (
        <p className="text-xs text-gray-400 italic">
          Using built-in defaults. Add questions below to override.
        </p>
      )}

      <div className="space-y-2">
        {(isCustom ? questions : []).map((q, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={q}
              onChange={e => update(i, e.target.value)}
              placeholder="e.g. How do I size the inlet eye?"
              className={INPUT_CLS}
            />
            <button onClick={() => remove(i)}
              className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {!isCustom && (
        <div className="space-y-1.5">
          {DEFAULT_STARTERS.map((q, i) => (
            <div key={i} className="flex items-center gap-2 opacity-50">
              <div className={`${INPUT_CLS} text-gray-500 cursor-default`}>{q}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <button onClick={add}
          className="flex items-center gap-1.5 text-sm text-bh-600 hover:text-bh-700 font-medium">
          <PlusIcon className="h-4 w-4" /> Add question
        </button>
        {isCustom && (
          <button onClick={handleReset}
            className="text-sm text-gray-400 hover:text-gray-600">
            Reset to defaults
          </button>
        )}
        <div className="flex-1" />
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 rounded-lg bg-bh-600 text-white text-sm font-medium
            hover:bg-bh-700 disabled:opacity-50 transition-colors">
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
        </button>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminSettingsPage() {
  const { copilotEnabled, setCopilotEnabled } = useSettingsStore();

  return (
    <>
      <TopBar title="Settings" />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-xl space-y-6">

          <Card>
            <CardHeader
              title="Feature Visibility"
              subtitle="Show or hide features for non-admin users. Admins always see everything."
            />
            <CardBody className="divide-y divide-gray-50">
              <ToggleRow
                icon={SparklesIcon}
                label="Pump Copilot"
                description="AI assistant panel in the sidebar. Disable during demos to focus on core engineering features."
                enabled={copilotEnabled}
                onChange={setCopilotEnabled}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="LLM Provider"
              subtitle="Configure the AI model used by the Pump Copilot. API key is stored server-side."
            />
            <CardBody>
              <LLMConfigCard />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Copilot Starter Questions"
              subtitle="Shown when the chat is empty. Leave blank to use the built-in defaults."
            />
            <CardBody>
              <CopilotStartersCard />
            </CardBody>
          </Card>

        </div>
      </main>
    </>
  );
}
